import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { UserPlus, Pencil, Trash, Shield, Eye, ShieldCheck, X } from "@phosphor-icons/react";

const PERMISSIONS = [
  { key: "view_sessions", label: "View Sessions" },
  { key: "manage_sessions", label: "Manage Sessions" },
  { key: "manage_coupons", label: "Manage Coupons" },
  { key: "manage_users", label: "Manage Users" },
  { key: "view_analytics", label: "View Analytics" },
];

const ROLE_DEFAULTS = {
  admin: PERMISSIONS.map(p => p.key),
  manager: ["view_sessions", "manage_sessions", "manage_coupons", "view_analytics"],
  associate: ["view_sessions", "view_analytics"],
};

const ROLE_DESCRIPTIONS = {
  admin: "Full access: sessions, integrations, coupons, prices, user access, API settings.",
  manager: "Sessions + analytics, Test Integration, Coupons, Live Prices.",
  associate: "View-only: sessions + analytics.",
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const roleBadge = (role) => {
  const map = {
    admin: "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40",
    manager: "bg-[#FFD60A]/15 text-[#FFD60A] border-[#FFD60A]/40",
    associate: "bg-[#64D2FF]/15 text-[#64D2FF] border-[#64D2FF]/40",
  };
  return map[role] || map.associate;
};

export default function UserManagement() {
  const { fetchWithAuth, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "associate", permissions: ROLE_DEFAULTS.associate });

  const loadUsers = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/users`);
      if (res.ok) setUsers(await res.json());
    } catch (err) {
      console.error("UserManagement.loadUsers failed", err);
    }
  }, [fetchWithAuth]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const openCreate = () => {
    setEditingUser(null);
    setForm({ email: "", password: "", name: "", role: "associate", permissions: [...ROLE_DEFAULTS.associate] });
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setForm({ email: u.email, password: "", name: u.name || "", role: u.role, permissions: [...(u.permissions || [])] });
    setShowModal(true);
  };

  const handleRoleChange = (role) => {
    setForm(f => ({ ...f, role, permissions: [...ROLE_DEFAULTS[role]] }));
  };

  const togglePerm = (key) => {
    setForm(f => {
      const has = f.permissions.includes(key);
      return { ...f, permissions: has ? f.permissions.filter(p => p !== key) : [...f.permissions, key] };
    });
  };

  const handleSave = async () => {
    try {
      const body = { email: form.email, name: form.name, role: form.role, permissions: form.permissions };
      if (form.password) body.password = form.password;
      if (!editingUser && !form.password) { toast.error("Password required for new user"); return; }

      const url = editingUser ? `${API_URL}/api/users/${editingUser.id}` : `${API_URL}/api/users`;
      const method = editingUser ? "PATCH" : "POST";

      const res = await fetchWithAuth(url, { method, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      toast.success(editingUser ? "User updated" : "User created");
      setShowModal(false);
      loadUsers();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/api/users/${u.id}`, { method: "DELETE" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      toast.success("User deleted");
      loadUsers();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleToggleActive = async (u) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/users/${u.id}`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: !u.is_active })
      });
      if (!res.ok) throw new Error("Failed");
      toast.success(u.is_active ? "User disabled" : "User enabled");
      loadUsers();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="user-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white">User Access Control</h2>
          <p className="text-xs text-[#666]">{users.length} user{users.length !== 1 ? "s" : ""} registered</p>
        </div>
        <button onClick={openCreate} data-testid="create-user-btn" className="flex items-center gap-2 px-4 py-2 bg-[#E85D24] text-white text-sm font-medium rounded hover:bg-[#E85D24]/85 transition-all">
          <UserPlus size={16} weight="bold" /> Add User
        </button>
      </div>

      {/* Users Table */}
      <div className="bg-[#121212] border border-[#2A2A2A] rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#2A2A2A] text-left">
              <th className="py-3 px-4 text-[#707070] font-medium text-xs">User</th>
              <th className="py-3 px-4 text-[#707070] font-medium text-xs">Role</th>
              <th className="py-3 px-4 text-[#707070] font-medium text-xs">Permissions</th>
              <th className="py-3 px-4 text-[#707070] font-medium text-xs">Status</th>
              <th className="py-3 px-4 text-[#707070] font-medium text-xs text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-b border-[#1A1A1A] hover:bg-[#0A0A0A]" data-testid={`user-row-${u.id}`}>
                <td className="py-3 px-4">
                  <p className="text-white font-medium">{u.name || u.email.split("@")[0]}</p>
                  <p className="text-[#666] text-xs">{u.email}</p>
                </td>
                <td className="py-3 px-4">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium border ${roleBadge(u.role)}`}>
                    {u.role}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <div className="flex flex-wrap gap-1">
                    {(u.permissions || []).map(p => (
                      <span key={p} className="px-1.5 py-0.5 bg-[#1A1A1A] text-[#888] text-[10px] rounded font-mono">{p}</span>
                    ))}
                  </div>
                </td>
                <td className="py-3 px-4">
                  <button onClick={() => handleToggleActive(u)} className={`text-xs font-medium px-2 py-0.5 rounded border ${u.is_active ? "bg-[#30D158]/15 text-[#30D158] border-[#30D158]/40" : "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40"}`}>
                    {u.is_active ? "Active" : "Disabled"}
                  </button>
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => openEdit(u)} className="p-1.5 hover:bg-[#2A2A2A] rounded transition-colors" data-testid={`edit-user-${u.id}`}>
                      <Pencil size={14} className="text-[#888]" />
                    </button>
                    {u.id !== currentUser?.id && (
                      <button onClick={() => handleDelete(u)} className="p-1.5 hover:bg-[#FF3B30]/20 rounded transition-colors" data-testid={`delete-user-${u.id}`}>
                        <Trash size={14} className="text-[#FF3B30]" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Role legend */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          { role: "admin", icon: ShieldCheck, desc: ROLE_DESCRIPTIONS.admin },
          { role: "manager", icon: Shield, desc: ROLE_DESCRIPTIONS.manager },
          { role: "associate", icon: Eye, desc: ROLE_DESCRIPTIONS.associate },
        ].map(r => (
          <div key={r.role} className="bg-[#121212] border border-[#2A2A2A] rounded p-3">
            <div className="flex items-center gap-2 mb-1">
              <r.icon size={16} className="text-[#E85D24]" />
              <span className={`px-2 py-0.5 rounded text-xs font-medium border ${roleBadge(r.role)}`}>{r.role}</span>
            </div>
            <p className="text-[11px] text-[#666]">{r.desc}</p>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="user-modal">
          <div className="bg-[#121212] border border-[#2A2A2A] rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-[#2A2A2A]">
              <h3 className="text-white font-semibold">{editingUser ? "Edit User" : "Create User"}</h3>
              <button onClick={() => setShowModal(false)} className="text-[#666] hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs text-[#888] mb-1">Name</label>
                <input data-testid="user-name-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#E85D24]" />
              </div>
              <div>
                <label className="block text-xs text-[#888] mb-1">Email</label>
                <input data-testid="user-email-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#E85D24]" />
              </div>
              <div>
                <label className="block text-xs text-[#888] mb-1">{editingUser ? "New Password (leave blank to keep)" : "Password"}</label>
                <input data-testid="user-password-input" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#E85D24]" />
              </div>
              <div>
                <label className="block text-xs text-[#888] mb-1">Role</label>
                <div className="flex gap-2">
                  {["admin", "manager", "associate"].map(r => (
                    <button key={r} onClick={() => handleRoleChange(r)}
                      className={`px-3 py-1.5 rounded text-xs font-medium border transition-all ${form.role === r ? "bg-[#E85D24]/20 border-[#E85D24] text-[#E85D24]" : "border-[#2A2A2A] text-[#666] hover:border-[#444]"}`}>
                      {r}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs text-[#888] mb-1">Permissions</label>
                <div className="flex flex-wrap gap-2">
                  {PERMISSIONS.map(p => (
                    <button key={p.key} onClick={() => togglePerm(p.key)}
                      className={`px-2 py-1 rounded text-[11px] font-mono border transition-all ${form.permissions.includes(p.key) ? "bg-[#30D158]/15 border-[#30D158]/40 text-[#30D158]" : "border-[#2A2A2A] text-[#555]"}`}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-[#2A2A2A]">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-[#888] hover:text-white transition-colors">Cancel</button>
              <button onClick={handleSave} data-testid="save-user-btn" className="px-4 py-2 bg-[#E85D24] text-white text-sm font-medium rounded hover:bg-[#E85D24]/85 transition-all">
                {editingUser ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
